import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FRAGMENT_STATUS, selectFragment } from '../features/fragmentsSlice.js';
import {
	fetchFragment,
	reinitFragmentScripts,
	scopeHtmlWidgetIds
} from '../utils/fragmentUtils';

// Monotonic counter backing each useFragmentLoader instance's stable suffix
// (see instanceSuffixRef below) — scopes a shared fragment's widget id to
// whichever specific mount (mega menu vs. drawer, etc.) is hydrating it.
let instanceIdCounter = 0;

/**
 * useFragmentLoader — lazy-fetches a fragment URL when the slot element
 * enters the scroll container's viewport. Caches results in Redux so
 * subsequent opens are instant (no re-fetch).
 *
 * A cached fragment's bundle still needs re-running against the DOM it renders
 * into (see reinitFragmentScripts) when this hook instance mounts with content
 * that was ALREADY cached by a DIFFERENT embed of the same url. The real case:
 * the same widget (e.g. model-selector) is embedded in both the mega menu and
 * the drawer; whichever mounts second finds the html already in Redux and must
 * re-run the bundle once to hydrate its own container (widget ids are scoped
 * per instance — see scopeHtmlWidgetIds). renderKey (returned below, used as
 * FragmentContent's dangerouslySetInnerHTML key) forces that DOM to be recreated
 * from the cached html immediately before reinitFragmentScripts runs, so the
 * bundle always sees pristine markup. This is a one-time, mount-time trigger,
 * bounded at ≤2 inits over the page's life for a dual-embedded widget — NOT a
 * per-reopen trigger (the panels are kept mounted; see below).
 *
 * There is a second, opt-in re-init trigger — a fragment becoming visible again
 * WITHOUT unmounting (a panel toggled with display:none / an `in` class rather
 * than unmounted). Re-running the bundle on every such reshow re-bootstraps the
 * embedded widget with no teardown of the prior generation, leaking DOM nodes +
 * listeners. Both production callers keep their fragment mounted for the page's
 * life — the mega menu (MegaMenuFragment) toggles display:none, and the drawer's
 * drilldown bodies (DrilldownPanel) are kept mounted and only toggle visibility —
 * so BOTH pass reinitOnReshow=false and this trigger is disabled. It is retained
 * (default true) only for the standalone/test path; no production caller enables
 * it.
 *
 * @param {string} url - fragment endpoint URL
 * @param {React.RefObject<HTMLElement>} slotRef - element to observe
 * @param {React.RefObject<HTMLElement>} scrollContainerRef - IntersectionObserver root
 * @param {object} [options] - opt-in/out behavior flags
 * @param {boolean} [options.reinitOnReshow=true] - when true, set up the reshow
 *   observer that bumps renderKey — forcing a fragment re-init — each time an
 *   already-mounted-but-hidden fragment becomes visible again. No production
 *   caller enables this: both the mega menu (MegaMenuFragment) and the drawer
 *   (DrawerNavFragment) keep their fragment mounted for the page's life and pass
 *   false, because re-initializing a still-mounted widget on every reopen
 *   re-bootstraps it with no teardown of the prior generation, leaking DOM nodes +
 *   listeners. The default stays true for standalone/test use; when false the
 *   reshow observer is never created.
 * @returns {{ status: string, html: string|null, renderKey: number }}
 */
const useFragmentLoader = (
	url,
	slotRef,
	scrollContainerRef,
	{ reinitOnReshow = true } = {}
) => {
	const dispatch = useDispatch();
	const { status, html, scripts, states } = useSelector((state) =>
		selectFragment(state, url)
	);
	const observerRef = useRef(null);
	const reinitObserverRef = useRef(null);
	const [renderKey, setRenderKey] = useState(0);

	// Stable per-mount suffix scoping this instance's widget id — see
	// scopeHtmlWidgetIds/scopeStates in fragmentUtils for why: the same
	// fragment url/content can be embedded in more than one place at once
	// (e.g. the mega menu and the drawer both referencing the same nav
	// item), and without a per-instance id the widget's own bootstrap can't
	// tell which mount's container to hydrate into.
	const instanceSuffixRef = useRef(null);
	if (instanceSuffixRef.current === null) {
		instanceIdCounter += 1;
		instanceSuffixRef.current = `frag${instanceIdCounter}`;
	}
	const suffix = instanceSuffixRef.current;

	const scopedHtml = useMemo(
		() => scopeHtmlWidgetIds(html, states, suffix),
		[html, states, suffix]
	);

	// Capture once at render time whether the fragment was already loaded when
	// this hook instance mounted. Initialized synchronously so the effect below
	// can use it without stale-closure concerns.
	const wasLoadedOnMount = useRef(status === FRAGMENT_STATUS.LOADED);

	// Request a one-time re-init when this instance mounts with content that was
	// ALREADY cached by a different embed of the same url (e.g. the mega menu
	// fetched the model-selector, now this drawer instance mounts). The fetch
	// path (fetchFragment) handles the first-ever load; this handles the
	// already-cached mount so the bundle runs once against this instance's own
	// container. NOT a per-reopen path — the panels are kept mounted, so this
	// fires at most once per instance. Bumping renderKey (rather than calling
	// reinitFragmentScripts directly) forces FragmentContent to recreate its
	// dangerouslySetInnerHTML DOM first — see the renderKey effect below for why.
	// Empty deps: run exactly once after the first committed render.
	useEffect(() => {
		if (!wasLoadedOnMount.current) return;
		setRenderKey((k) => k + 1);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Request a re-init when an already-mounted-but-hidden fragment becomes
	// visible again. Opt-in via reinitOnReshow; no production caller enables it
	// (both keep the fragment mounted — see the hook doc), so in practice this
	// observer is never created outside standalone/test use. When enabled: only
	// set up once loaded, observing the same slot element used for the
	// fetch-trigger observer. The observer's first callback reports whatever the
	// current state already is right after load — not a real hide-then-show
	// transition — so it's skipped; every callback after that is a genuine
	// visibility change.
	useEffect(() => {
		// Opt-out (both production callers): never set up the reshow observer, so
		// a still-mounted fragment becoming visible again does not re-init.
		if (!reinitOnReshow) return undefined;
		if (typeof window === 'undefined') return undefined;
		if (status !== FRAGMENT_STATUS.LOADED) return undefined;

		const el = slotRef?.current;
		if (!el) return undefined;

		const root = scrollContainerRef?.current ?? null;
		let sawFirstCallback = false;

		const observer = new IntersectionObserver(
			(entries) => {
				if (!sawFirstCallback) {
					sawFirstCallback = true;
					return;
				}
				if (!entries[0].isIntersecting) return;
				setRenderKey((k) => k + 1);
			},
			{ root, threshold: 0 }
		);

		reinitObserverRef.current = observer;
		observer.observe(el);

		return () => {
			observer.disconnect();
			reinitObserverRef.current = null;
		};
	}, [status, slotRef, scrollContainerRef, reinitOnReshow]);

	// Runs after the DOM has been recreated in response to renderKey (see
	// FragmentContent, which keys its dangerouslySetInnerHTML div on
	// renderKey) — reinitFragmentScripts always sees pristine markup.
	// renderKey === 0 is the initial value, not a reinit request; the
	// first-load path (fetchFragment) already handles that case itself.
	useEffect(() => {
		if (renderKey === 0) return;
		reinitFragmentScripts(scripts, states, suffix);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [renderKey]);

	useEffect(() => {
		// SSR guard
		if (typeof window === 'undefined') return;

		// Only idle should set up the fetch-trigger observer. LOADED/ERROR are
		// the obvious skips (cache hit / already failed), but LOADING must be
		// skipped too: fetchFragment dispatches fragmentFetchStarted
		// synchronously, before its network call resolves, and status is a
		// dependency of this effect — so idle-to-loading re-runs it while the
		// slot element is still in view. Recreating the observer here would
		// report that already-intersecting state immediately, firing a
		// second, concurrent fetchFragment for the same url.
		if (status !== FRAGMENT_STATUS.IDLE) return;

		const el = slotRef?.current;
		if (!el) return;

		const root = scrollContainerRef?.current ?? null;

		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries[0].isIntersecting) return;

				// Disconnect immediately — only fetch once
				observer.disconnect();
				observerRef.current = null;

				fetchFragment(url, dispatch, suffix);
			},
			{ root, threshold: 0 }
		);

		observerRef.current = observer;
		observer.observe(el);

		// eslint-disable-next-line consistent-return
		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
		};
	}, [url, slotRef, scrollContainerRef, dispatch, status, suffix]);

	return { status, html: scopedHtml, renderKey };
};

export default useFragmentLoader;
